import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FileText, Lock, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Trava de assinatura do Módulo Fiscal.
 *
 * O fiscal é um ADICIONAL cobrado à parte, não um degrau de plano — por isso
 * não usa o PlanGate, que é hierárquico (starter < start < completo) e
 * liberaria o módulo de graça pra toda a base do plano completo.
 *
 * Isto aqui é só a camada de experiência. A trava DE VERDADE é no servidor:
 * toda rota fiscal responde 402 sem a assinatura. Esconder botão não é
 * controle de acesso.
 */

const API = "/api";
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const FiscalCtx = createContext({ ativo: false, carregando: true, pedirAssinatura: () => {} });

export function useFiscal() {
  return useContext(FiscalCtx);
}

export function FiscalProvider({ children }) {
  const [ativo, setAtivo] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [popupAberto, setPopupAberto] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/fiscal/addon`, auth());
        if (vivo) setAtivo(!!data?.ativo);
      } catch {
        // 402 (sem assinatura) ou qualquer falha: trata como não contratado.
        // O servidor é quem decide de verdade a cada chamada.
        if (vivo) setAtivo(false);
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  const pedirAssinatura = useCallback(() => setPopupAberto(true), []);
  const valor = useMemo(
    () => ({ ativo, carregando, pedirAssinatura }),
    [ativo, carregando, pedirAssinatura],
  );

  return (
    <FiscalCtx.Provider value={valor}>
      {children}
      <PopupAssinatura aberto={popupAberto} onFechar={() => setPopupAberto(false)} />
    </FiscalCtx.Provider>
  );
}

function PopupAssinatura({ aberto, onFechar }) {
  return (
    <Dialog open={aberto} onOpenChange={onFechar}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <FileText className="w-4.5 h-4.5 text-primary" />
            </div>
            Módulo Fiscal
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            A emissão de nota fiscal é um adicional do Núcleo e ainda não está ativa
            para esta loja.
          </p>
          <div className="bg-muted/40 rounded-xl p-4 space-y-2.5">
            {[
              "NFC-e emitida sozinha ao concluir a venda",
              "Cupom já sai com chave de acesso e QR Code",
              "Cadastro fiscal do cardápio em massa, por categoria",
              "Cancelamento automático quando o pedido é cancelado",
            ].map((item) => (
              <p key={item} className="text-[13px] flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                <span>{item}</span>
              </p>
            ))}
          </div>
          <p className="text-[11.5px] text-muted-foreground">
            Fale com o suporte para ativar. Em breve dá para contratar direto em Assinatura.
          </p>
          <Button onClick={onFechar} className="w-full h-10">Entendi</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Envolve um trecho de tela que exige a assinatura. Sem ela, o conteúdo fica
 * esmaecido e sem clique, e qualquer toque abre o popup — em vez de sumir, o
 * lojista vê o que está perdendo.
 */
export function FiscalLock({ children, className = "" }) {
  const { ativo, carregando, pedirAssinatura } = useFiscal();
  if (carregando || ativo) return children;
  return (
    <div className={`relative ${className}`}>
      <div className="opacity-40 pointer-events-none select-none">{children}</div>
      <button
        type="button"
        onClick={pedirAssinatura}
        aria-label="Assinar o Módulo Fiscal"
        className="absolute inset-0 z-10 flex items-center justify-center rounded-xl
                   bg-background/10 hover:bg-background/25 transition-colors group"
      >
        <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card border shadow-sm
                         text-[11.5px] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          <Lock className="w-3.5 h-3.5" /> Requer o Módulo Fiscal
        </span>
      </button>
    </div>
  );
}
